/* eslint-disable no-underscore-dangle */
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import ContentLoading from "src/Components/Loading/contentLoading";
import PopupComponent from "src/Components/Popup/popup";
import Spinner from "src/Components/Spinner";
import DataTableControlled from "src/Components/DataTableControlled";
import {
  DESELECT_PAGE_ALL,
  SELECT_PAGE_ALL,
} from "src/Components/DataTableControlled/consts";
import { getListingData } from "src/Components/DataTableControlled/utils";
import {
  AsyncAutocompleteSelectFilter,
  AutocompleteSelectFilter,
  DateFilter,
  SelectFilter,
  UuidFilter,
} from "src/Components/DataTable/filters";
import {
  getDateCell,
  mapValueFromOptions,
} from "src/Components/DataTable/commonCells";
import BusinessIdColumn from "src/Components/DataTable/businessIdColumn";
import { API_TOTALREWARD_SERVICE } from "src/utils/Api";
import { dynamicNotification } from "src/utils/Notifications";
import __ from "src/utils/Translations";
import translateLabels from "src/utils/Translations/translateLabels";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { useListTotalRewardsPerksOptions } from "src/Pages/Company/TotalReward/api/usePerks";
import {
  useDeleteTotalRewardsPerkUsers,
  useExportTotalRewardsPerkUsers,
} from "src/Pages/Company/TotalReward/api/usePerkUsers";
import { useListTotalRewardsPerkUsersPeriods } from "src/Pages/Company/TotalReward/api/usePerkUsersPeriods";
import { getUsersOptionsFetchMethod } from "src/Pages/Company/TotalReward/hooks/useQueryTotalRewardUsers";
import {
  totalRewardPerkUsersRead,
  totalRewardPerkUsersWrite,
} from "src/utils/RoleBasedSecurity/permissions";
import useIsImpersonated from "src/utils/hooks/useIsImpersonated";
import getUserConfirmationPopup from "src/utils/system/DOM/getUserConfirmationPopup";
import moment from "moment";

export default function PerkUsersDataTable() {
  const tableApiRef = useRef(null);

  const isImpersonated = useIsImpersonated();

  const companyId = useCompanyId();
  const { data: perks } = useListTotalRewardsPerksOptions(companyId);
  const { data: periods } = useListTotalRewardsPerkUsersPeriods(
    companyId,
    true
  );

  const [massActionSelect, setMassActionSelect] = useState({
    excluded: [],
    included: [],
  });

  const [modalMessage, setModalMessage] = useState(false);
  const [deleted, setDeleted] = useState(0);
  const mutation = useDeleteTotalRewardsPerkUsers(companyId);

  const massDelete = useCallback(
    (confirm) => {
      if (!confirm) {
        setMassActionSelect({
          excluded: [],
          included: [],
        });
      } else if (massActionSelect.included?.length > 0) {
        mutation.mutate(massActionSelect.included, {
          onSuccess(data) {
            setModalMessage(data?.message);

            setDeleted((count) => count + 1);

            setMassActionSelect({
              excluded: [],
              included: [],
            });
          },
          onError(err) {
            dynamicNotification(
              err.message || __("Nie udało się usunąć"),
              "error",
              undefined,
              undefined,
              10000
            );
          },
        });
      }
    },
    [massActionSelect.included, mutation]
  );

  const onMassDeleteHandler = () => {
    getUserConfirmationPopup(
      <>
        {__("Liczba wybranych pozycji")}: {massActionSelect.included.length}
        <br />
        {__("Czy na pewno chcesz usunąć zaznaczone elementy?")}{" "}
        {__("Wykonana operacja jest nieodwracalna.")}
      </>,
      massDelete,
      __("Usuwanie przydzielonych benefitow.")
    );
  };

  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const fetchData = useCallback(
    async (filters, page, pageSize, sort) => {
      const options = {
        params: { tenant: companyId },
      };
      if (sort?.key === "perk") {
        options.params[`order[perk.title]`] = sort.value;
      }
      if (sort?.key === "user") {
        options.params[`order[user.lastName]`] = sort.value;
        options.params[`order[user.firstName]`] = sort.value;
      }
      const localFilters = filters.reduce((result, current) => {
        if (current.id === "import.created.at") {
          if (current.value.from) {
            result.push({
              id: "import.created.at[after]",
              value: moment(current.value.from).format("YYYY-MM-DD"),
            });
          }
          if (current.value.to) {
            result.push({
              id: "import.created.at[before]",
              value: moment(current.value.to).format("YYYY-MM-DD"),
            });
          }
        } else {
          result.push(current);
        }

        return result;
      }, []);
      const { data: newData, count: newCount } = await getListingData(
        API_TOTALREWARD_SERVICE,
        `/omb/perk-users`,
        localFilters,
        page,
        pageSize,
        sort,
        options,
        mockData,
        true
      );
      if (newData) {
        setData(newData);
        setCount(newCount);
      }
    },
    [companyId]
  );

  const useExportPerkUsers = useExportTotalRewardsPerkUsers();

  const exportPerkUsers = useCallback(
    (filters, sort) => {
      const order = {};
      if (sort?.key === "perk") {
        order[`order[perk.title]`] = sort.value;
      } else if (sort?.key === "user") {
        order[`order[user.lastName]`] = sort.value;
        order[`order[user.firstName]`] = sort.value;
      } else if (sort) {
        order[`order[${sort.key}]`] = sort.value;
      }

      useExportPerkUsers.mutate(
        {
          tenant: companyId,
          ...filters.reduce(
            (filters, filter) => ({ ...filters, [filter.id]: filter.value }),
            {}
          ),
          ...order,
        },
        {
          onError: (err) => {
            dynamicNotification(
              err.message || __("Nie udało się pobrać danych"),
              "error"
            );
          },
        }
      );
    },
    [companyId, useExportPerkUsers]
  );

  const exportButton = useMemo(() => {
    const canExport = tableApiRef.current?.filters.some(({ id }) =>
      ["user", "perk", "period"].includes(id)
    );
    return {
      id: "exportBtn",
      permission: totalRewardPerkUsersRead,
      text: useExportPerkUsers.isPending ? "Pobieranie pliku" : "Eksportuj",
      tooltip: isImpersonated
        ? null
        : !canExport &&
          __(
            "W celu wyeksportowania danych należy wskazać co najmniej jeden z dostępnych filtrów: Benefit, Pracownik lub Okres. Wybór pozostałych filtrów jest opcjonalny."
          ),
      title: canExport ? __("Pobierz plik Excel") : undefined,
      disabled: isImpersonated || useExportPerkUsers.isPending || !canExport,
      onClick: () => {
        if (!isImpersonated && canExport) {
          exportPerkUsers(
            tableApiRef.current.filters,
            tableApiRef.current.sort
          );
        }
      },
    };
  }, [exportPerkUsers, isImpersonated, useExportPerkUsers.isPending]);

  return (
    <>
      <ContentLoading
        show={mutation.isPending}
        message={
          <>
            {Spinner}
            <br />
            {__("Trwa usuwanie danych. Pozostań na stronie.")}
          </>
        }
        messageStyle={{
          position: "absolute",
          top: "20%",
          width: "100%",
          textAlign: "center",
          fontSize: "1.5rem",
        }}
      >
        <DataTableControlled
          id="PerkUsersListing"
          apiRef={tableApiRef}
          fetchData={fetchData}
          data={data}
          count={count}
          massActionSelect={massActionSelect}
          setMassActionSelect={setMassActionSelect}
          reloadDependencies={[deleted]}
          selectionOptions={translateLabels(ROW_SELECT_OPTIONS)}
          isRowSelectable={(row) => !!row.deletable}
          filterable
          buttons={[
            {
              id: "massDeleteBtn",
              color: "warning",
              text: "Usuń wybrane",
              tooltip: isImpersonated
                ? undefined
                : "Możliwe jest usunięcie jedynie benefitów ze źródła plik.",
              disabled: isImpersonated || !massActionSelect.included.length,
              onClick: () => {
                if (!isImpersonated) onMassDeleteHandler();
              },
            },
            exportButton,
          ]}
          columns={[
            {
              Header: __("ID"),
              accessor: "id",
              minWidth: 155,
              Cell: BusinessIdColumn,
              Filter: UuidFilter,
            },
            {
              Header: __("Benefit"),
              accessor: "perk",
              minWidth: 200,
              Filter: AutocompleteSelectFilter(perks || []),
              Cell: ({ row }) => row._original.perk?.title,
            },
            {
              Header: __("Widoczny"),
              accessor: "perk.status",
              minWidth: 100,
              Filter: SelectFilter(translateLabels(statusBooleanOptions)),
              Cell: mapValueFromOptions(
                translateLabels(statusBooleanOptions),
                "perk.status"
              ),
            },
            {
              Header: __("Typ"),
              accessor: "perk.financial",
              minWidth: 100,
              Filter: SelectFilter(translateLabels(financialBooleanOptions)),
              Cell: mapValueFromOptions(
                translateLabels(financialBooleanOptions),
                "perk.financial"
              ),
            },
            {
              Header: __("Pracownik"),
              accessor: "user",
              minWidth: 200,
              Filter: AsyncAutocompleteSelectFilter((filterValue) =>
                getUsersOptionsFetchMethod(companyId, filterValue)
              ),
              Cell: ({ row }) =>
                `${row._original.user?.lastName} ${
                  row._original.user?.firstName
                } ${
                  row._original.user?.fk ? `(${row._original.user?.fk})` : ""
                }`,
            },
            {
              Header: __("Okres"),
              accessor: "period",
              Filter: SelectFilter(periods),
              minWidth: 110,
            },
            {
              Header: __("Wartość"),
              accessor: "value",
              minWidth: 110,
            },
            {
              Header: __("Źródło importu"),
              accessor: "source",
              Filter: SelectFilter(translateLabels(importSources)),
              Cell: mapValueFromOptions(
                translateLabels(importSources),
                "source"
              ),
              minWidth: 120,
            },
            {
              Header: __("Data importu"),
              accessor: "import.created.at",
              Filter: DateFilter(),
              minWidth: 150,
              Cell: getDateCell("import.created.at"),
            },
          ]}
        />
      </ContentLoading>
      <Modal
        isOpen={!!modalMessage}
        toggle={() => setModalMessage(false)}
        unmountOnClose
      >
        <ModalHeader toggle={() => setModalMessage(false)}>
          {__("Pomyślnie usunięto przydzielone benefity!")}
        </ModalHeader>
        <ModalBody>
          <div style={{ whiteSpace: "pre-wrap" }}>{modalMessage}</div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => setModalMessage(false)}>
            {__("Ok")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

const mockData = [];

const statusBooleanOptions = [
  {
    value: true,
    label: "Widoczny",
  },
  {
    value: false,
    label: "Niewidoczny",
  },
];

const importSources = [
  {
    value: "manual",
    label: "Ręczny",
  },
  {
    value: "point_banks",
    label: "Auto banki pkt",
  },
  {
    value: "subscription_benefits",
    label: "Auto abonamenty",
  },
];

const financialBooleanOptions = [
  {
    value: true,
    label: "Finansowy",
  },
  {
    value: false,
    label: "Niefinansowy",
  },
];

const ROW_SELECT_OPTIONS = [
  {
    value: SELECT_PAGE_ALL,
    label: "Zaznacz wszystkie na tej stronie",
  },
  {
    value: DESELECT_PAGE_ALL,
    label: "Odznacz wszystkie na tej stronie",
  },
];
